import { Alert, Button, MenuItem, Skeleton, TextField } from "@mui/material";
import { Controller } from "react-hook-form";
import ErrorBanner from "components/Common/ErrorBanner/ErrorBanner";
import AutoComplete from "components/Common/Autocomplete/Autocomplete";
import { useGetUserDetailsQuery } from "features/api/userApi";
import { models } from "types/api/viewModels";
import { supportCaseFormSchema, supportProblemTypes } from "types/forms/supportCaseForm";
import { getFormFieldProps } from "utils/form";
import { isDevEnv } from "constants/environment";
import useSupportCaseForm from "../hooks/useSupportCaseForm";
import SupportCaseAttachments from "../components/SupportCaseAttachments";
import * as PageStyles from "./SupportPage.styles";
import * as Styles from "./SupportCasePage.styles";

interface SupportCasePageProps {
	isOrderHelp: boolean;
}

const SupportCaseForm = ({ user, isOrderHelp }: SupportCasePageProps & { user: models["UserViewModel"] }) => {
	const {
		methods,
		account,
		order,
		orderSearch,
		files,
		accountsQuery,
		ordersQuery,
		submission,
		submit,
		changeAccount,
		setOrder,
		setOrderSearch,
		setFiles
	} = useSupportCaseForm(user, isOrderHelp);
	const { register, formState, control, watch } = methods;
	const formFieldData = { register, formState, schema: supportCaseFormSchema };
	const isSubmitting = formState.isSubmitting || submission.isLoading;
	const description = watch("description");
	const descriptionFieldProps = getFormFieldProps({ name: "description", ...formFieldData });
	const error = submission.error;
	const errorCode = error && "status" in error ? String(error.status) : undefined;

	return (
		<>
			{isDevEnv && import.meta.env.VITE_APP_MOCKED && (
				<Alert severity="info">Development mock: submitting this form does not create a Salesforce case.</Alert>
			)}
			{error && (
				<div role="alert">
					<ErrorBanner
						errorMessaging={
							<div>
								<div>We&apos;re sorry, we couldn’t process your request.</div>
								<p>
									Something happened behind the scenes. Please try the following to proceed with your
									support request:
								</p>
								<ul>
									<li>Remove and reattach any image files</li>
									<li>Submit your request again</li>
									<li>Try to submit your request later</li>
								</ul>
								<p>Error code: {errorCode ?? "REQUEST_FAILED"}</p>
							</div>
						}
					/>
				</div>
			)}
			<Styles.Form
				noValidate
				onSubmit={submit}
				aria-busy={isSubmitting}
			>
				<Styles.HelpText>Required Field (*)</Styles.HelpText>
				<Styles.Fields disabled={isSubmitting}>
					{isOrderHelp && (
						<Controller
							name="problemType"
							control={control}
							render={({ field }) => (
								<TextField
									{...getFormFieldProps({
										name: "problemType",
										formState,
										schema: supportCaseFormSchema
									})}
									{...field}
									label="Type of Problem"
									InputLabelProps={{ shrink: true }}
									select
									size="small"
									fullWidth
								>
									{supportProblemTypes.map((type) => (
										<MenuItem
											key={type}
											value={type}
										>
											{type}
										</MenuItem>
									))}
								</TextField>
							)}
						/>
					)}
					<TextField
						{...getFormFieldProps({ name: "name", ...formFieldData })}
						label="Name"
						size="small"
						fullWidth
						InputLabelProps={{ shrink: true }}
					/>
					<TextField
						{...getFormFieldProps({ name: "email", ...formFieldData })}
						label="Email"
						type="email"
						size="small"
						fullWidth
						InputLabelProps={{ shrink: true }}
					/>
					<AutoComplete
						label="Account"
						dataTestId="support-case-account"
						id="support-case-account"
						options={accountsQuery.currentData ?? []}
						value={account}
						getOptionLabel={(option: models["CustomerAccountViewModel"]) =>
							[option.accountNumber, option.accountName].filter(Boolean).join(" ")
						}
						getOptionKey={(option) => option.accountId}
						isOptionEqualToValue={(option, selected) => option.accountId === selected.accountId}
						onChange={(_event, selected) => changeAccount(selected)}
						isLoading={accountsQuery.isFetching}
						disabled={isSubmitting || accountsQuery.isFetching}
						isError={accountsQuery.isError}
						errorText="Unable to load accounts. Please retry."
						noOptionsText="No accounts available."
					/>
					{accountsQuery.isError && (
						<Button
							type="button"
							onClick={() => {
								accountsQuery.refetch();
							}}
						>
							Retry accounts
						</Button>
					)}
					<AutoComplete
						label="Order Number"
						dataTestId="support-case-order"
						id="support-case-order"
						options={ordersQuery.currentData ?? []}
						value={order}
						inputValue={orderSearch || order?.orderNumber || ""}
						virtualize
						getOptionLabel={(option) => option.orderNumber ?? ""}
						getOptionKey={(option) => option.orderId}
						isOptionEqualToValue={(option, selected) => option.orderId === selected.orderId}
						onChange={(_event, selected) => {
							setOrder(selected);
							setOrderSearch("");
						}}
						onInputChange={(_event, value, reason) => {
							if (reason === "input" || reason === "clear") {
								setOrder(null);
								setOrderSearch(value);
							}
						}}
						filterOptions={(options) =>
							options.filter((option) =>
								option.orderNumber?.toLowerCase().includes(orderSearch.trim().toLowerCase())
							)
						}
						isLoading={ordersQuery.isFetching}
						disabled={isSubmitting}
						isError={ordersQuery.isError}
						errorText="Unable to load orders. Please retry."
						noOptionsText={ordersQuery.isFetching ? "Loading orders..." : "No matching orders."}
						helperText="If the problem is with a specific order, please provide the order number."
					/>
					{ordersQuery.isError && (
						<Button
							type="button"
							onClick={() => {
								ordersQuery.refetch();
							}}
						>
							Retry orders
						</Button>
					)}
					<TextField
						{...descriptionFieldProps}
						label={isOrderHelp ? "Describe the problem" : "Describe the Problem"}
						helperText={
							<Styles.DescriptionHelp>
								{descriptionFieldProps.helperText}
								<Styles.CharacterCount>({description.length}/500)</Styles.CharacterCount>
							</Styles.DescriptionHelp>
						}
						multiline
						rows={3}
						size="small"
						fullWidth
						InputLabelProps={{ shrink: true }}
						inputProps={{ maxLength: 500 }}
					/>
					<TextField
						{...getFormFieldProps({ name: "url", ...formFieldData, required: false })}
						label={isOrderHelp ? "URL or Webpage with the Problem" : "URL or Website with the Problem"}
						type="url"
						size="small"
						fullWidth
						InputLabelProps={{ shrink: true }}
					/>
				</Styles.Fields>
				<Styles.HelpText>
					{isOrderHelp
						? "Attaching screenshots of any error message you got or the URL of the page when the problem happened will greatly speed your support request."
						: "Attaching screenshots of the error message you got or the URL of the page when the problem happened will greatly speed your support request."}
				</Styles.HelpText>
				<Styles.Fields disabled={isSubmitting}>
					<SupportCaseAttachments
						files={files}
						onChange={setFiles}
						disabled={isSubmitting}
					/>
					<Styles.SubmitButton
						type="submit"
						variant="contained"
						disabled={
							isSubmitting ||
							accountsQuery.isFetching ||
							ordersQuery.isFetching ||
							accountsQuery.isError ||
							ordersQuery.isError
						}
					>
						SUBMIT REQUEST
					</Styles.SubmitButton>
				</Styles.Fields>
			</Styles.Form>
		</>
	);
};

const SupportCasePage = ({ isOrderHelp }: SupportCasePageProps) => {
	const { currentData: user, isLoading, isError, refetch } = useGetUserDetailsQuery();
	return (
		<PageStyles.Page>
			<PageStyles.Header>
				<PageStyles.Title>Support Request</PageStyles.Title>
			</PageStyles.Header>
			<PageStyles.Content>
				<Styles.Subtitle>{isOrderHelp ? "I need help with an order." : "I have a problem."}</Styles.Subtitle>
				<Styles.Panel>
					{isLoading && (
						<Styles.Fields aria-label="Loading support form">
							{Array.from({ length: isOrderHelp ? 7 : 6 }, (_, i) => (
								<Skeleton
									key={i}
									height={40}
									variant="rectangular"
								/>
							))}
						</Styles.Fields>
					)}
					{(isError || (!isLoading && !user)) && (
						<>
							<ErrorBanner errorMessaging="Unable to load your information. Please try again." />
							<Button
								onClick={() => {
									refetch();
								}}
							>
								Retry
							</Button>
						</>
					)}
					{!isLoading && !isError && user && (
						<SupportCaseForm
							key={`${user.email}-${isOrderHelp}`}
							user={user}
							isOrderHelp={isOrderHelp}
						/>
					)}
				</Styles.Panel>
			</PageStyles.Content>
		</PageStyles.Page>
	);
};

export default SupportCasePage;
